import { CHESTS, chaferEmojis, getItem, larvae, uiEmojis, type Element, type ItemType, type Lang } from '@fakerpg/core';
import { tierStyle } from './palette.js';

/** Semantic asset keys (03-assets §4). */
export type UiIcon = 'fakecoin' | 'lootcoin' | 'gems' | 'energy' | 'xp' | 'attack' | 'hp' | 'level' | 'pass' | 'nugget' | 'orb' | 'icekamas' | 'page' | 'legendary';

export type AssetKey =
  | { kind: 'item'; type: ItemType; id: number }
  | { kind: 'emoji'; id: string; name: string; animated: boolean }
  | { kind: 'ui'; name: UiIcon }
  | { kind: 'element'; element: Element | string }
  | { kind: 'chest'; name: string }
  | { kind: 'dofus'; token: string; tier?: string; dim?: boolean }
  | { kind: 'league'; rank: number; token: string }
  | { kind: 'ornament'; token: string; side: 'left' | 'right'; seed: string }
  | { kind: 'chafer'; crit: boolean }
  | { kind: 'larva'; index: number };

const EMOJI_TOKEN = /^<(a?):([A-Za-z0-9_~]+):(\d{15,21})>$/;

export function parseEmojiToken(token: string): { id: string; name: string; animated: boolean } | null {
  const m = EMOJI_TOKEN.exec(token.trim());
  if (!m) return null;
  return { animated: m[1] === 'a', name: m[2] ?? '', id: m[3] ?? '' };
}

/** Bot UI emoji constant (utils/emojis.ts export) behind each UI icon. */
const UI_EMOJI_EXPORT: Record<UiIcon, string> = {
  fakecoin: 'devise',
  lootcoin: 'lootCoins',
  gems: 'shopCoins',
  energy: 'energyEmoji',
  xp: 'xpEmoji',
  attack: 'attackEmoji',
  hp: 'hpEmoji',
  level: 'levelEmoji',
  pass: 'tokenEmoji',
  nugget: 'nuggetsEmoji',
  orb: 'magicalOrbEmoji',
  icekamas: 'iceKamasEmoji',
  page: 'shushuPageEmoji',
  legendary: 'legendaryCoin',
};

const UI_BY_EMOJI_ID = new Map<string, UiIcon>(
  (Object.entries(UI_EMOJI_EXPORT) as [UiIcon, string][]).flatMap(([icon, name]) => {
    const id = parseEmojiToken(uiEmojis[name] ?? '')?.id;
    return id ? [[id, icon] as [string, UiIcon]] : [];
  }),
);

/** The UI icon behind a bot emoji id (FakeCoin, level…), so bot text gets the right generated art. */
export function uiIconForEmoji(id: string): UiIcon | null {
  return UI_BY_EMOJI_ID.get(id) ?? null;
}

/** Discord emoji id for a key, when the original bot has one. */
export function discordEmojiFor(key: AssetKey): { id: string; animated: boolean } | null {
  switch (key.kind) {
    case 'emoji':
      return { id: key.id, animated: key.animated };
    case 'item': {
      const item = getItem(key.type, key.id);
      return item ? parseEmojiToken(item.emoji) : null;
    }
    case 'ui': {
      const token = uiEmojis[UI_EMOJI_EXPORT[key.name]];
      return token ? parseEmojiToken(token) : null;
    }
    case 'chest': {
      const token = CHESTS[key.name]?.emoji;
      return token ? parseEmojiToken(token) : null;
    }
    case 'dofus':
    case 'league':
    case 'ornament':
      return parseEmojiToken(key.token);
    case 'chafer':
      return parseEmojiToken(key.crit ? chaferEmojis.crit : chaferEmojis.plain);
    case 'larva':
      return parseEmojiToken(larvae[key.index]?.emoji ?? '');
    case 'element': {
      const token = uiEmojis[`${String(key.element).toLowerCase()}Emoji`] ?? (key.element === 'Multi' ? uiEmojis['multiElementEmoji'] : undefined);
      return token ? parseEmojiToken(token) : null;
    }
  }
}

export function discordEmojiUrl(id: string, animated: boolean, size = 96): string {
  return `https://cdn.discordapp.com/emojis/${id}.${animated ? 'gif' : 'webp'}?size=${size}&quality=lossless`;
}

export function labelFor(key: AssetKey, lang: Lang): string {
  switch (key.kind) {
    case 'item':
      return getItem(key.type, key.id)?.name[lang] ?? '';
    case 'emoji':
      return key.name;
    case 'ui':
      return key.name;
    case 'element':
      return String(key.element);
    case 'chest':
      return key.name;
    case 'dofus':
      return parseEmojiToken(key.token)?.name ?? 'Dofus';
    case 'league':
      return parseEmojiToken(key.token)?.name ?? 'League';
    case 'ornament':
      return key.seed;
    case 'chafer':
      return key.crit ? 'Chafer crit' : 'Chafer';
    case 'larva':
      return larvae[key.index]?.name[lang] ?? '';
  }
}

export function tierColor(tier: number): string {
  return tierStyle(tier).base;
}
